import { useState, useRef, useEffect } from 'react'
import { ChevronDown, MessageSquare, Plus, Trash2 } from 'lucide-react'
import { useAgentConversations, useDeleteConversation } from '@/services/agent/queries'
import { cn } from '@/lib/utils'
import { formatDistanceToNow } from 'date-fns'

type Props = {
    activeId: string | null
    onSelect: (id: string | null) => void
    onNewChat: () => void
}

/**
 * Compact dropdown variant of ConversationSidebar — designed for the slide-in panel
 * where horizontal space is scarce. Shows the active conversation as a button;
 * clicking opens a popover list of recent conversations.
 */
export default function ConversationSwitcher({ activeId, onSelect, onNewChat }: Props) {
    const [isOpen, setIsOpen] = useState(false)
    const ref = useRef<HTMLDivElement>(null)
    const { data, isLoading } = useAgentConversations()
    const deleteConversation = useDeleteConversation()

    useEffect(() => {
        if (!isOpen) return
        const handleClickOutside = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) {
                setIsOpen(false)
            }
        }
        document.addEventListener('mousedown', handleClickOutside)
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [isOpen])

    const activeConv = data?.conversations.find(c => c.id === activeId)
    const activeLabel = activeConv?.title || 'New conversation'

    const handleDelete = (e: React.MouseEvent, id: string) => {
        e.stopPropagation()
        deleteConversation.mutate(id)
        if (activeId === id) onSelect(null)
    }

    return (
        <div ref={ref} className='relative min-w-0 flex-1'>
            <button
                type='button'
                onClick={() => setIsOpen(prev => !prev)}
                className='flex w-full items-center gap-1.5 rounded-md px-2 py-1.5 text-left text-sm text-slate-700 transition-colors hover:bg-slate-100'
            >
                <MessageSquare className='h-3.5 w-3.5 shrink-0 text-slate-400' />
                <span className='min-w-0 flex-1 truncate text-xs font-medium'>{activeLabel}</span>
                <ChevronDown className='h-3.5 w-3.5 shrink-0 text-slate-400' />
            </button>

            {isOpen && (
                <div className='absolute left-0 right-0 top-full z-50 mt-1 max-h-[60vh] overflow-y-auto rounded-lg border border-slate-200 bg-white shadow-xl'>
                    <button
                        type='button'
                        onClick={() => {
                            onNewChat()
                            setIsOpen(false)
                        }}
                        className='flex w-full items-center gap-2 border-b border-slate-100 px-3 py-2 text-xs font-medium text-primary-main transition-colors hover:bg-primary-main/5'
                    >
                        <Plus className='h-3.5 w-3.5' />
                        New chat
                    </button>
                    {isLoading ? (
                        <div className='space-y-1 p-2'>
                            {[1, 2, 3].map(i => (
                                <div key={i} className='h-9 animate-pulse rounded-md bg-slate-100' />
                            ))}
                        </div>
                    ) : data?.conversations.length === 0 ? (
                        <div className='px-3 py-6 text-center text-xs text-slate-400'>No conversations yet</div>
                    ) : (
                        <div className='py-1'>
                            {data?.conversations.map(conv => (
                                <button
                                    key={conv.id}
                                    type='button'
                                    onClick={() => {
                                        onSelect(conv.id)
                                        setIsOpen(false)
                                    }}
                                    className={cn(
                                        'group flex w-full items-start gap-2 px-3 py-2 text-left transition-colors',
                                        activeId === conv.id
                                            ? 'bg-primary-main/5 text-slate-900'
                                            : 'text-slate-600 hover:bg-slate-50'
                                    )}
                                >
                                    <MessageSquare
                                        className={cn(
                                            'mt-0.5 h-3.5 w-3.5 shrink-0',
                                            activeId === conv.id ? 'text-primary-main' : 'text-slate-400'
                                        )}
                                    />
                                    <div className='min-w-0 flex-1'>
                                        <p className='truncate text-xs font-medium leading-tight'>{conv.title}</p>
                                        <p className='mt-0.5 text-[10px] text-slate-400'>
                                            {formatDistanceToNow(new Date(conv.lastMessageAt), { addSuffix: true })}
                                        </p>
                                    </div>
                                    <button
                                        type='button'
                                        onClick={e => handleDelete(e, conv.id)}
                                        className='shrink-0 rounded p-1 opacity-0 transition-opacity hover:bg-slate-200 group-hover:opacity-100'
                                    >
                                        <Trash2 className='h-3 w-3 text-slate-400' />
                                    </button>
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    )
}
